import { CATALOG_CATEGORY_ORDER, CONNECTOR_CATALOG, getConnectorAvailability, } from "./catalog.js";
function deskSourcesFor(entry) {
    return entry.deskSources ?? [{ id: entry.id, label: entry.label }];
}
/**
 * Every desk-attachable source derived from the connector catalog, in
 * CATALOG_CATEGORY_ORDER (honoring per-category serviceOrder overrides), so
 * consumers render a stable order without re-sorting.
 */
export function deskSourceCandidates() {
    const out = [];
    for (const { key, serviceOrder } of CATALOG_CATEGORY_ORDER) {
        const entries = CONNECTOR_CATALOG.filter((e) => e.category === key);
        const ordered = serviceOrder
            ? [...entries].sort((a, b) => serviceOrder.indexOf(a.id) - serviceOrder.indexOf(b.id))
            : entries;
        for (const entry of ordered) {
            for (const ds of deskSourcesFor(entry)) {
                out.push({
                    source: ds.id,
                    label: ds.label,
                    category: entry.category,
                    serviceType: entry.id,
                    kind: entry.readinessClassification === "tool_only"
                        ? "tool_only"
                        : "syncing",
                    personalOnly: ds.personalOnly ?? false,
                });
            }
        }
    }
    return out;
}
/** Live syncing sources that current product guidance may truthfully offer. */
export function liveDeskSourceCandidates() {
    return deskSourceCandidates().filter((candidate) => {
        if (candidate.kind !== "syncing")
            return false;
        const entry = CONNECTOR_CATALOG.find((item) => item.id === candidate.serviceType);
        return entry !== undefined && getConnectorAvailability(entry) === "live";
    });
}
/**
 * The single allowlist for Connections proposed during Team-room onboarding.
 * A Connection belongs here only when it is live and projects a source a Team
 * can read. Tool-only services remain in recipe.tools until they have a
 * separate, truthful tool-health model.
 */
export const TEAM_BLUEPRINT_CONNECTIONS = (() => {
    const options = [];
    const seen = new Set();
    for (const candidate of liveDeskSourceCandidates()) {
        if (seen.has(candidate.serviceType))
            continue;
        const entry = CONNECTOR_CATALOG.find((item) => item.id === candidate.serviceType);
        if (!entry || getConnectorAvailability(entry) !== "live")
            continue;
        seen.add(entry.id);
        options.push({
            id: entry.id,
            label: entry.label,
            personal: entry.dataDomain === "personal",
        });
    }
    return options;
})();
const TEAM_BLUEPRINT_CONNECTION_IDS = new Set(TEAM_BLUEPRINT_CONNECTIONS.map((connection) => connection.id));
const TEAM_BLUEPRINT_SOURCE_ALIASES = {
    calendar: "gcal",
    google_calendar: "gcal",
};
/** Map a service id or granular source alias onto the reviewed Connection id. */
export function normalizeTeamBlueprintConnectionId(value) {
    const raw = value.trim().toLowerCase();
    if (!raw)
        return null;
    if (TEAM_BLUEPRINT_CONNECTION_IDS.has(raw))
        return raw;
    const source = TEAM_BLUEPRINT_SOURCE_ALIASES[raw] ?? raw;
    const candidate = deskSourceCandidates().find((item) => item.kind === "syncing" && item.source === source);
    return candidate && TEAM_BLUEPRINT_CONNECTION_IDS.has(candidate.serviceType)
        ? candidate.serviceType
        : null;
}
/**
 * Legacy service-type aliases that appear in older connected_services /
 * binding rows for the personal Google sources.
 */
const PERSONAL_ONLY_ALIASES = new Set([
    "google_calendar",
    "calendar",
    "drive",
]);
/**
 * Memoized: the catalog is compile-time constant, and isPersonalOnlyDeskSource
 * runs per source in per-request paths (proposal listing/validation, binding
 * checks) — rebuilding the candidate list on every call is pure waste.
 * Lazy so module-evaluation order stays irrelevant.
 */
let personalOnlyIdSet = null;
function personalOnlyIds() {
    personalOnlyIdSet ??= new Set([
        ...deskSourceCandidates()
            .filter((candidate) => candidate.personalOnly)
            .map((candidate) => candidate.source),
        ...PERSONAL_ONLY_ALIASES,
    ]);
    return personalOnlyIdSet;
}
/** Every canonical and legacy source id that must never have workspace scope. */
export function personalOnlyDeskSourceIds() {
    return [...personalOnlyIds()];
}
export function isPersonalOnlyDeskSource(source) {
    return personalOnlyIds().has(source.trim());
}
//# sourceMappingURL=deskSources.js.map